import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/button";

const COLORS = [
  { name: "Red", hex: "#ef4444" },
  { name: "Blue", hex: "#3b82f6" },
  { name: "Green", hex: "#10b981" },
  { name: "Yellow", hex: "#eab308" },
  { name: "Purple", hex: "#8b5cf6" },
  { name: "Orange", hex: "#f97316" },
];
const GAME_DURATION = 30; // seconds
const TOTAL_ROUNDS = 20;

function getStroop() {
  const wordColor = COLORS[Math.floor(Math.random() * COLORS.length)];
  let displayColor;
  do { displayColor = COLORS[Math.floor(Math.random() * COLORS.length)]; } while (displayColor.name === wordColor.name);
  // 50% congruent, 50% incongruent
  const congruent = Math.random() > 0.5;
  return {
    word: wordColor.name,
    displayColor: congruent ? wordColor.hex : displayColor.hex,
    correctAnswer: congruent ? wordColor.name : displayColor.name,
    actualColor: congruent ? wordColor : displayColor,
  };
}

export default function ColorWordStroop({ onComplete }) {
  const [state, setState] = useState("idle");
  const [current, setCurrent] = useState(null);
  const [round, setRound] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef(null);

  const nextRound = useCallback(() => {
    setCurrent(getStroop());
    setFeedback(null);
  }, []);

  const startGame = useCallback(() => {
    setState("playing");
    setRound(0);
    setCorrect(0);
    setWrong(0);
    setTimeLeft(GAME_DURATION);
    nextRound();

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setState("finished");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [nextRound]);

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleAnswer = (colorName) => {
    if (state !== "playing" || !current) return;
    const isCorrect = colorName === current.actualColor.name;
    if (isCorrect) {
      setCorrect(prev => prev + 1);
      setFeedback("correct");
    } else {
      setWrong(prev => prev + 1);
      setFeedback("wrong");
    }
    setRound(prev => prev + 1);
    setTimeout(nextRound, 300);
  };

  const score = Math.max(0, correct * 50 - wrong * 20);
  const accuracy = round > 0 ? Math.round((correct / round) * 100) : 0;

  if (state === "finished") {
    return (
      <div data-testid="stroop-result" className="text-center py-8 space-y-6">
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center mx-auto shadow-xl">
          <span className="text-3xl font-bold text-white">{score}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Mental Agility Score</h3>
          <p className="text-gray-500">{correct} correct | {wrong} wrong | {accuracy}% accuracy</p>
          <p className="text-sm text-gray-400">{round} rounds in {GAME_DURATION}s</p>
        </div>
        <Button data-testid="save-stroop-score" onClick={() => onComplete(score, GAME_DURATION)} className="bg-emerald-600 hover:bg-emerald-700">
          Save Score & Return
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="stroop-game" className="flex flex-col items-center py-6 space-y-6">
      {state === "idle" && (
        <>
          <p className="text-sm text-gray-600 text-center max-w-md">
            A color word will appear in a different color. Tap the button matching the <strong>INK COLOR</strong>, not the word itself. This is the Stroop test — it trains mental agility and focus.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-center border">
            <p className="text-sm text-gray-700 mb-2">Example:</p>
            <span className="text-3xl font-black" style={{ color: "#3b82f6" }}>Red</span>
            <p className="text-xs text-gray-500 mt-2">The word says "Red" but the color is <strong>Blue</strong>. Tap Blue!</p>
          </div>
          <Button data-testid="start-stroop" onClick={startGame} className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8 py-3">Start Game</Button>
        </>
      )}

      {state === "playing" && current && (
        <>
          <div className="flex items-center justify-between w-full max-w-xs">
            <span className="text-sm font-bold text-gray-700">Round {round + 1}</span>
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${timeLeft <= 5 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-gray-100 text-gray-700'}`}>
              {timeLeft}s
            </div>
            <span className="text-sm text-emerald-600 font-semibold">{correct} correct</span>
          </div>

          <div className={`py-6 px-12 rounded-2xl transition-all ${feedback === 'correct' ? 'bg-emerald-50' : feedback === 'wrong' ? 'bg-red-50' : 'bg-white'} border-2 border-gray-100`}>
            <span className="text-5xl font-black" style={{ color: current.displayColor }}>
              {current.word}
            </span>
          </div>
          <p className="text-xs text-gray-500">Tap the <strong>INK COLOR</strong>, not the word!</p>

          <div className="grid grid-cols-3 gap-2 max-w-xs">
            {COLORS.map(c => (
              <button
                key={c.name}
                onClick={() => handleAnswer(c.name)}
                className="px-4 py-3 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95 shadow-md"
                style={{ backgroundColor: c.hex }}
              >
                {c.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
